import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ShoppingCart, Users } from '@/components/icons';
import { useCheckout } from '@/context/CheckoutContext';
import { useNotifications } from '@/context/NotificationContext';
import { useUserMenu } from '@/context/UserMenuContext';
import { useHeaderOffset } from '@/hooks/useHeaderOffset';
import { useSession } from '@/context/SessionContext';
import { getOpenCartsForUser } from '@/lib/supabase/carts';
import { getTempCart, getTempCartAsItems } from '@/lib/tempCart';
import { getStoreById } from '@/lib/supabase/stores';
import { getCartsSharedWithMe } from '@/lib/supabase/sharedCarts';
import { formatCurrency } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import {
  ACTIVE_SHARED_CART_STATE_CHANGED,
  getActiveCartPreference,
  setActiveCartPreference,
  getActiveCartSnapshot,
  setActiveCartSnapshot,
  getActiveCartSnapshotByStoreSlug,
  setActiveSharedCartThemePriority,
} from '@/lib/activeCartPreference';

const ACTIVE_CART_FLOATING_DEBUG = true;
const debugFloatingActiveCart = (...args: any[]) => {
  if (!ACTIVE_CART_FLOATING_DEBUG) return;
  console.log('[FloatingCartButton:ActiveCartDebug]', ...args);
};

const floatingToErrorMessage = (error: unknown): string => {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  const maybeObj = error as any;
  return maybeObj?.message || maybeObj?.error_description || maybeObj?.details || JSON.stringify(maybeObj);
};

export default function FloatingCartButton() {
  const { openSidebar: openCheckoutSidebar, sidebarOpen: checkoutSidebarOpen } = useCheckout();
  const { sidebarOpen: notificationsSidebarOpen } = useNotifications();
  const { sidebarOpen: userMenuSidebarOpen } = useUserMenu();
  const { bottomNavHeight, isDesktop } = useHeaderOffset();
  const { session } = useSession();
  const location = useLocation();
  const [totalUnits, setTotalUnits] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [activeCartIsShared, setActiveCartIsShared] = useState(false);
  const [badgePulse, setBadgePulse] = useState(false);
  const [firstAppear, setFirstAppear] = useState(false);
  const [forcedVisible, setForcedVisible] = useState(false);
  const [forcedUnits, setForcedUnits] = useState(0);
  const [forcedTotal, setForcedTotal] = useState(0);
  const [forcedStoreName, setForcedStoreName] = useState('');
  const [forcedStoreId, setForcedStoreId] = useState('');
  const [normalStoreName, setNormalStoreName] = useState('');
  const [normalStoreId, setNormalStoreId] = useState('');
  const [openCartIds, setOpenCartIds] = useState<string[]>([]);
  const prevUnitsRef = useRef(0);
  const hasShownBefore = useRef(false);
  const forcedVisibleRef = useRef(false);
  const [isRendered, setIsRendered] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isAtPageEnd, setIsAtPageEnd] = useState(false);
  const [mobileNavTopInset, setMobileNavTopInset] = useState<number | null>(null);
  const [storePublicAislesReached, setStorePublicAislesReached] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const realtimeReloadRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLandscape, setIsLandscape] = useState(false);

  const isMinibidRoute = location.pathname === '/24hs';
  const isCollaborativePoolRoute = location.pathname === '/tiendas/pool-productos';
  const isRadioRoute = location.pathname === '/radio' || location.pathname.startsWith('/radio/');
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const rootSlug = pathSegments[0] || '';
  const excludedRootSlugs = new Set([
    'inicio',
    'auth',
    'login',
    'signup',
    'chats',
    'chat',
    'tour',
    'map',
    'mapa',
    'checkout',
    'collections',
    'notificaciones',
    'configuracion',
    'transporte',
    'search-results',
    'print-room',
    'activity-log',
    'cargafacil',
    'notas',
    'orden',
    'products',
    'edit-product',
    'tiendas',
    'mercadopago',
  ]);
  const isStorePublicRoute = pathSegments.length === 1 && !excludedRootSlugs.has(rootSlug) && rootSlug !== '24hs';
  const isAllowedFloatingRoute = isMinibidRoute || isStorePublicRoute;
  const forcedModeActive = forcedVisible && isMinibidRoute;

  const displayedUnits = isMinibidRoute ? forcedUnits : totalUnits;
  const displayedTotal = isMinibidRoute ? forcedTotal : cartTotal;
  const displayedStoreName = isMinibidRoute ? forcedStoreName : normalStoreName;
  const displayedStoreId = isMinibidRoute ? forcedStoreId : normalStoreId;

  const getStoreDisplayLabel = (name: string) => {
    const normalized = (name || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return '';

    // Si es suficientemente corto, mostrar completo
    if (normalized.length <= 22) return normalized;

    // Si tiene varias palabras, mostrar siglas (máx 6 letras)
    const words = normalized.split(' ').filter(Boolean);
    if (words.length >= 2) {
      const acronym = words
        .map((word) => word[0])
        .join('')
        .toUpperCase();
      if (acronym.length >= 2 && acronym.length <= 6) return acronym;
      if (acronym.length > 6) return acronym.slice(0, 6);
    }

    // Si es una sola palabra o siglas largas, truncar
    return `${normalized.slice(0, 18).trimEnd()}...`;
  };

  const storeDisplayLabel = getStoreDisplayLabel(displayedStoreName);

  const loadCart = useCallback(async () => {
    try {
      if (!isAllowedFloatingRoute) {
        setOpenCartIds([]);
        setTotalUnits(0);
        setCartTotal(0);
        setActiveCartIsShared(false);
        setNormalStoreName('');
        setNormalStoreId('');
        return;
      }

      if (isMinibidRoute) {
        // On Minibid, this button must be driven only by forced store-specific payloads.
        setTotalUnits(0);
        setCartTotal(0);
        setActiveCartIsShared(false);
        setNormalStoreName('');
        setNormalStoreId('');
        return;
      }

      if (session?.user?.id) {
        const [carts, sharedWithMe] = await Promise.all([
          getOpenCartsForUser(session.user.id),
          getCartsSharedWithMe(),
        ]);
        setOpenCartIds((carts || []).map((c: any) => c?.id).filter(Boolean));

        const pathSegments = location.pathname.split('/').filter(Boolean);
        const getStoreData = (cart: any) => {
          const store = cart?.stores;
          const normalizedStore = Array.isArray(store) ? store[0] : store;
          return {
            id: cart?.store_id || normalizedStore?.id || '',
            name: normalizedStore?.name || '',
            slug: normalizedStore?.slug || '',
          };
        };

        const sumCart = (cart: any) => {
          const items = cart?.cart_items || [];
          const units = items.reduce((s: number, i: any) => s + (i.quantity || 0), 0);
          const total = items.reduce((s: number, i: any) => s + ((i.unit_price || 0) * (i.quantity || 0)), 0);
          return { units, total };
        };

        const normalizeSharedCart = (shared: any) => ({
          id: `shared-cart-${shared.id}`,
          shared_cart_id: shared.id,
          store_id: shared.store_id,
          stores: {
            id: shared.store_id,
            name: shared.store_name || '',
            slug: shared.store_slug || '',
          },
          cart_items: (shared.items || []).map((item: any) => ({
            id: `shared-${shared.id}-${item.product_id}`,
            product_id: item.product_id,
            quantity: Number(item.quantity) || 0,
            unit_price: Number(item.unit_price) || 0,
          })),
        });

        const allCandidateCarts = [
          ...(carts || []),
          ...((sharedWithMe || []).map(normalizeSharedCart)),
        ];

        const cartsForCurrentStore = allCandidateCarts.filter((cart: any) => {
          const { slug } = getStoreData(cart);
          return slug && slug === rootSlug;
        });

        if (isStorePublicRoute && cartsForCurrentStore.length > 0) {
          const storeId = getStoreData(cartsForCurrentStore[0]).id;
          const preferredCartId = storeId ? getActiveCartPreference(storeId) : null;
          const preferredCart = preferredCartId
            ? cartsForCurrentStore.find((cart: any) => cart.id === preferredCartId)
            : null;
          const snapshot = storeId ? getActiveCartSnapshot(storeId) : null;
          const fallbackCart = cartsForCurrentStore.find((cart: any) => !cart?.shared_cart_id) || cartsForCurrentStore[0];
          const activeCart = preferredCart || fallbackCart;

          debugFloatingActiveCart('resolve-store-active', {
            rootSlug,
            storeId,
            preferredCartId,
            resolvedCartId: activeCart?.id || null,
            candidates: cartsForCurrentStore.map((cart: any) => ({ id: cart.id, isShared: !!cart.shared_cart_id })),
          });

          if (preferredCartId && !preferredCart && snapshot?.cartId === preferredCartId && snapshot?.isShared) {
            debugFloatingActiveCart('resolve-store-active:using-shared-snapshot', {
              storeId,
              preferredCartId,
              snapshotUnits: snapshot.units,
              snapshotTotal: snapshot.total,
            });

            setTotalUnits(Number(snapshot.units || 0));
            setCartTotal(Number(snapshot.total || 0));
            setActiveCartIsShared(true);
            setNormalStoreName(snapshot.storeName || getStoreData(cartsForCurrentStore[0]).name || '');
            setNormalStoreId(storeId || '');

            // Shared carts now use direct access instead of tokens
            // No token-based operations needed

            return;
          }

          if (activeCart) {
            const { units, total } = sumCart(activeCart);
            const targetStore = getStoreData(activeCart);
            const ownerCollaborativeActive = Boolean(
              snapshot?.isShared
              && snapshot?.cartId
              && snapshot.cartId === activeCart.id
            );

            setTotalUnits(units);
            setCartTotal(total);
            setActiveCartIsShared(!!activeCart?.shared_cart_id || ownerCollaborativeActive);
            setNormalStoreName(targetStore.name || '');
            setNormalStoreId(targetStore.id || '');

            if (!preferredCartId && targetStore.id) {
              setActiveCartPreference(targetStore.id, activeCart.id);
            }
            return;
          }
        }

        if (isStorePublicRoute) {
          const slugSnapshot = getActiveCartSnapshotByStoreSlug(rootSlug);
          if (slugSnapshot?.isShared) {
            debugFloatingActiveCart('resolve-store-active:slug-snapshot-shared-only', {
              rootSlug,
              snapshotStoreId: slugSnapshot.storeId,
              snapshotCartId: slugSnapshot.cartId,
            });

            setTotalUnits(Number(slugSnapshot.units || 0));
            setCartTotal(Number(slugSnapshot.total || 0));
            setActiveCartIsShared(true);
            setNormalStoreName(slugSnapshot.storeName || '');
            setNormalStoreId(slugSnapshot.storeId || '');

            // Shared carts now use direct access instead of tokens
            // No token-based operations needed

            return;
          }
        }

        const allItems = allCandidateCarts.flatMap((c: any) => c.cart_items || []);
        const validItems = allItems.filter((i: any) => Number(i.quantity || 0) > 0);
        const units = validItems.reduce((s: number, i: any) => s + Number(i.quantity || 0), 0);
        const total = validItems.reduce((s: number, i: any) => s + (Number(i.unit_price || 0) * Number(i.quantity || 0)), 0);
        setTotalUnits(units);
        setCartTotal(total);
        setActiveCartIsShared(false);

        const matchedBySlug = allCandidateCarts.find((cart: any) => {
          const { slug } = getStoreData(cart);
          return slug && pathSegments.includes(slug);
        });

        const targetCart = matchedBySlug || (allCandidateCarts.length === 1 ? allCandidateCarts[0] : null);
        if (targetCart) {
          const targetStore = getStoreData(targetCart);
          setNormalStoreName(targetStore.name || '');
          setNormalStoreId(targetStore.id || '');
        } else {
          setNormalStoreName('');
          setNormalStoreId('');
        }
      } else {
        setOpenCartIds([]);
        const tempCart = getTempCart();
        const tempItems = getTempCartAsItems(tempCart?.store_id);
        const validTempItems = tempItems.filter((i: any) => Number(i.quantity || 0) > 0);
        const units = validTempItems.reduce((s, i) => s + Number(i.quantity || 0), 0);
        const total = validTempItems.reduce((s, i) => s + (Number(i.unit_price || 0) * Number(i.quantity || 0)), 0);
        setTotalUnits(units);
        setCartTotal(total);
        setActiveCartIsShared(false);

        if (tempCart?.store_id) {
          const store = await getStoreById(tempCart.store_id);
          setNormalStoreName(store?.name || '');
          setNormalStoreId(tempCart.store_id);
        } else {
          setNormalStoreName('');
          setNormalStoreId('');
        }
      }
    } catch {
      // ignore
    }
  }, [isAllowedFloatingRoute, isMinibidRoute, session?.user?.id, location.pathname, rootSlug, isStorePublicRoute]);
  const queueRealtimeLoadCart = useCallback(() => {
    if (realtimeReloadRef.current) return;
    realtimeReloadRef.current = setTimeout(() => {
      realtimeReloadRef.current = null;
      void loadCart();
    }, 50);
  }, [loadCart]);

  // Load on mount and when session changes
  useEffect(() => {
    if (!isAllowedFloatingRoute) return;
    void loadCart();
  }, [isAllowedFloatingRoute, loadCart]);

  // Listen to local cart state events.
  useEffect(() => {
    if (!isAllowedFloatingRoute || isMinibidRoute) return;

    const handler = () => {
      debugFloatingActiveCart('event:cart-refresh');
      queueRealtimeLoadCart();
    };
    window.addEventListener('cart-updated', handler);
    window.addEventListener('cart-synced', handler);
    window.addEventListener('cart-active-changed', handler);
    window.addEventListener(ACTIVE_SHARED_CART_STATE_CHANGED, handler as EventListener);

    const handleStorage = (event: StorageEvent) => {
      if (!event.key) return;
      if (event.key.startsWith('active_cart_') || event.key.startsWith('active_shared_cart_')) {
        handler();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('cart-updated', handler);
      window.removeEventListener('cart-synced', handler);
      window.removeEventListener('cart-active-changed', handler);
      window.removeEventListener(ACTIVE_SHARED_CART_STATE_CHANGED, handler as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [isAllowedFloatingRoute, isMinibidRoute, queueRealtimeLoadCart]);

  // Consolidated realtime updates for owner carts, shared carts, and membership changes.
  useEffect(() => {
    if (!isAllowedFloatingRoute || isMinibidRoute) return;
    if (!session?.user?.id) return;

    let disposed = false;
    let activeChannel: any = null;

    const setupRealtimeSubscriptions = async () => {
      try {
        const { data: memberCartRows, error: memberCartError } = await supabase
          .from('cart_access')
          .select('cart_id')
          .eq('user_id', session.user.id)
          .eq('access_type', 'shared_with')
          .then((result) => ({
            data: (result.data || []).map((row: any) => row.cart_id),
            error: result.error,
          }));

        if (memberCartError) {
          throw memberCartError;
        }

        if (disposed) return;

        const watchedCartIds = Array.from(
          new Set([...(openCartIds || []), ...((memberCartRows as string[]) || [])].filter(Boolean))
        );

        const channel = supabase
          .channel(`floating-cart-live-${session.user.id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'carts',
              filter: `user_id=eq.${session.user.id}`,
            },
            () => queueRealtimeLoadCart()
          )
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'cart_access',
              filter: `user_id=eq.${session.user.id}`,
            },
            () => queueRealtimeLoadCart()
          );

        watchedCartIds.forEach((cartId: string) => {
          channel.on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'cart_items',
              filter: `cart_id=eq.${cartId}`,
            },
            () => queueRealtimeLoadCart()
          );
          channel.on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'carts',
              filter: `id=eq.${cartId}`,
            },
            () => queueRealtimeLoadCart()
          );
        });

        channel.subscribe();
        activeChannel = channel;
      } catch (error) {
        console.error('[FloatingCartButton] Error setting up realtime subscriptions:', error);
      }
    };

    void setupRealtimeSubscriptions();

    return () => {
      disposed = true;
      if (realtimeReloadRef.current) {
        clearTimeout(realtimeReloadRef.current);
        realtimeReloadRef.current = null;
      }
      if (activeChannel) {
        void supabase.removeChannel(activeChannel);
      }
    };
  }, [isAllowedFloatingRoute, isMinibidRoute, session?.user?.id, openCartIds, queueRealtimeLoadCart]);

  // Simple rule: when the people icon is shown next to the cart button, force Jacaranda.
  useEffect(() => {
    const shouldUseSharedColor = Boolean(isRendered && activeCartIsShared);
    setActiveSharedCartThemePriority(shouldUseSharedColor);
  }, [isRendered, activeCartIsShared]);

  // Polling fallback in case realtime is delayed.
  useEffect(() => {
    if (!isAllowedFloatingRoute || isMinibidRoute) return;
    if (!session?.user?.id) return;
    const interval = setInterval(() => {
      queueRealtimeLoadCart();
    }, 1500);
    return () => clearInterval(interval);
  }, [isAllowedFloatingRoute, isMinibidRoute, session?.user?.id, queueRealtimeLoadCart]);

  useEffect(() => {
    if (!isMinibidRoute) {
      return;
    }

    const handleForcedCartButton = (event: Event) => {
      const customEvent = event as CustomEvent<{
        visible?: boolean;
        units?: number;
        total?: number;
        storeName?: string;
        storeId?: string;
        isShared?: boolean;
      }>;
      const nextVisible = Boolean(customEvent.detail?.visible);
      const rawUnits = customEvent.detail?.units;
      const rawTotal = customEvent.detail?.total;
      const rawStoreName = customEvent.detail?.storeName;
      const rawStoreId = customEvent.detail?.storeId;
      const nextIsShared = !!customEvent.detail?.isShared;

      // Prevent first render with invalid values while waiting for a valid payload.
      if (nextVisible && (!Number.isFinite(rawUnits) || !Number.isFinite(rawTotal))) {
        return;
      }

      const nextUnits = Number.isFinite(rawUnits) ? Math.max(0, Math.trunc(rawUnits ?? 0)) : 0;
      const nextTotal = Number.isFinite(rawTotal) ? Math.max(0, rawTotal ?? 0) : 0;
      const nextStoreName = typeof rawStoreName === 'string' ? rawStoreName.trim() : '';
      const nextStoreId = typeof rawStoreId === 'string' ? rawStoreId.trim() : '';

      setForcedVisible(nextVisible);
      // Only update displayed values when showing — keep last values during exit so the
      // total doesn't flash to $0 before the close animation finishes.
      if (nextVisible) {
        setForcedUnits(nextUnits);
        setForcedTotal(nextTotal);
        setForcedStoreName(nextStoreName);
        setForcedStoreId(nextStoreId);
        setActiveCartIsShared(nextIsShared);
      }

      if (nextVisible && !forcedVisibleRef.current) {
        setFirstAppear(false);
        setTimeout(() => setFirstAppear(true), 10);
        setTimeout(() => setFirstAppear(false), 600);
      }

      forcedVisibleRef.current = nextVisible;
    };

    window.addEventListener('minibid-store-cart-cta', handleForcedCartButton as EventListener);
    return () => {
      window.removeEventListener('minibid-store-cart-cta', handleForcedCartButton as EventListener);
    };
  }, [isMinibidRoute]);

  useEffect(() => {
    if (isMinibidRoute) return;

    // Prevent stale Minibid-forced CTA from bleeding into other store views.
    setForcedVisible(false);
    setForcedUnits(0);
    setForcedTotal(0);
    setForcedStoreName('');
    setForcedStoreId('');
    forcedVisibleRef.current = false;
  }, [isMinibidRoute]);

  // Animate badge on change
  useEffect(() => {
    if (displayedUnits > 0 || forcedModeActive) {
      if (!hasShownBefore.current) {
        setFirstAppear(false);
        setTimeout(() => setFirstAppear(true), 10);
        setTimeout(() => setFirstAppear(false), 600);
        hasShownBefore.current = true;
      } else if (prevUnitsRef.current !== displayedUnits) {
        setBadgePulse(false);
        setTimeout(() => setBadgePulse(true), 10);
        setTimeout(() => setBadgePulse(false), 400);
      }
      prevUnitsRef.current = displayedUnits;
    } else {
      prevUnitsRef.current = 0;
      // Keep first-appear disabled after initial mount so quantity changes remain silent.
    }
  }, [displayedUnits, forcedModeActive]);

  useEffect(() => {
    if (!isStorePublicRoute) {
      setStorePublicAislesReached(false);
      return;
    }

    let rafId = 0;

    const getVisibleHeaderOffset = () => {
      const appHeader = document.querySelector('header.app-header') as HTMLElement | null;
      if (!appHeader) return 0;

      const hiddenAttr = appHeader.getAttribute('data-header-hidden');
      if (hiddenAttr === 'true') return 0;

      const rect = appHeader.getBoundingClientRect();
      const clampedHeight = Math.max(rect.height, 0);
      const visibleAttr = appHeader.getAttribute('data-header-visible');
      if (visibleAttr === 'true') return clampedHeight;

      return Math.max(Math.min(rect.bottom, clampedHeight), 0);
    };

    const evaluateAislesVisibility = () => {
      const aislesStart = document.querySelector('[data-store-public-aisles-start="true"]') as HTMLElement | null;
      if (!aislesStart) {
        setStorePublicAislesReached(false);
        return;
      }

      const rect = aislesStart.getBoundingClientRect();
      const visibleHeaderOffset = getVisibleHeaderOffset();
      const reached = rect.top <= visibleHeaderOffset + 8;
      setStorePublicAislesReached((prev) => (prev === reached ? prev : reached));
    };

    const scheduleEvaluation = () => {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(evaluateAislesVisibility);
    };

    scheduleEvaluation();
    const settleTimeout = setTimeout(scheduleEvaluation, 120);

    window.addEventListener('scroll', scheduleEvaluation, { passive: true });
    window.addEventListener('resize', scheduleEvaluation);

    return () => {
      clearTimeout(settleTimeout);
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', scheduleEvaluation);
      window.removeEventListener('resize', scheduleEvaluation);
    };
  }, [isStorePublicRoute]);

  const hasVisibleCart = isMinibidRoute ? forcedModeActive : totalUnits > 0;
  const allowEndTransform = false;
  const blockedByStorePublicAisles = isStorePublicRoute && !storePublicAislesReached;

  const shouldBeVisible =
    isAllowedFloatingRoute && !( !hasVisibleCart || checkoutSidebarOpen || notificationsSidebarOpen || userMenuSidebarOpen || blockedByStorePublicAisles);

  useEffect(() => {
    if (shouldBeVisible) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setIsClosing(false);
      setIsRendered(true);
      return;
    }

    if (!isRendered) return;

    setIsClosing(true);
    hideTimeoutRef.current = setTimeout(() => {
      setIsRendered(false);
      setIsClosing(false);
      hideTimeoutRef.current = null;
    }, 450);
  }, [isRendered, shouldBeVisible]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const updateOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    updateOrientation();
    window.addEventListener('resize', updateOrientation);
    return () => {
      window.removeEventListener('resize', updateOrientation);
    };
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setMobileNavTopInset(null);
      return;
    }

    let rafId = 0;
    let settleTimeout: ReturnType<typeof setTimeout> | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const readBottomNavInset = () => {
      const nav = document.querySelector('nav.app-bottom-nav') as HTMLElement | null;
      if (!nav) {
        setMobileNavTopInset(null);
        return;
      }

      const rect = nav.getBoundingClientRect();
      if (rect.height <= 0) return;

      const insetFromViewportBottom = Math.max(0, Math.round(window.innerHeight - rect.top));
      setMobileNavTopInset((prev) => (prev === insetFromViewportBottom ? prev : insetFromViewportBottom));
    };

    const scheduleRead = () => {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(readBottomNavInset);
    };

    scheduleRead();
    settleTimeout = setTimeout(scheduleRead, 160);

    const nav = document.querySelector('nav.app-bottom-nav') as HTMLElement | null;
    if (nav && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleRead);
      resizeObserver.observe(nav);
    }

    window.addEventListener('scroll', scheduleRead, { passive: true });
    window.addEventListener('resize', scheduleRead);
    window.visualViewport?.addEventListener('resize', scheduleRead);

    return () => {
      window.cancelAnimationFrame(rafId);
      if (settleTimeout) clearTimeout(settleTimeout);
      window.removeEventListener('scroll', scheduleRead);
      window.removeEventListener('resize', scheduleRead);
      window.visualViewport?.removeEventListener('resize', scheduleRead);
      resizeObserver?.disconnect();
    };
  }, [isDesktop]);

  useEffect(() => {
    if (!allowEndTransform) {
      setIsAtPageEnd(false);
      return;
    }

    const updateIsAtPageEnd = () => {
      const doc = document.documentElement;
      // If the page isn't meaningfully taller than the viewport (only the spacer
      // pushes it over), don't trigger the wide-end transform — it would appear
      // immediately on short pages and cover content.
      if (doc.scrollHeight <= window.innerHeight + 100) {
        setIsAtPageEnd(false);
        return;
      }
      const scrollBottom = window.scrollY + window.innerHeight;
      const distanceToBottom = doc.scrollHeight - scrollBottom;
      // Hysteresis avoids flicker/reposition jumps caused by tiny layout shifts near the end.
      setIsAtPageEnd((prev) => (prev ? distanceToBottom <= 120 : distanceToBottom <= 24));
    };

    updateIsAtPageEnd();
    requestAnimationFrame(updateIsAtPageEnd);
    const settleTimeout = window.setTimeout(updateIsAtPageEnd, 120);
    window.addEventListener('scroll', updateIsAtPageEnd, { passive: true });
    window.addEventListener('resize', updateIsAtPageEnd);
    return () => {
      window.clearTimeout(settleTimeout);
      window.removeEventListener('scroll', updateIsAtPageEnd);
      window.removeEventListener('resize', updateIsAtPageEnd);
    };
  }, [allowEndTransform, location.pathname]);

  const showWideEndButton = allowEndTransform && isAtPageEnd;
  const isWideDesktop = showWideEndButton && isDesktop;
  const isMobileLandscape = !isDesktop && isLandscape;
  const pageEndSpacerHeight = allowEndTransform ? (isDesktop ? 56 : isMobileLandscape ? 20 : 96) : 0;
  const desktopViewportInset = 24;
  const mobileFloatingGap = isMobileLandscape ? '0.75rem' : '1rem';
  const storePublicRouteExtraGap = isStorePublicRoute ? '0.5rem' : '0rem';

  if (isCollaborativePoolRoute || isRadioRoute || !isAllowedFloatingRoute || !isRendered) {
    return null;
  }

  const horizontalModeExtraBottom = showWideEndButton ? 14 : 0;
  const bottomOffset = (isWideDesktop ? 2 : desktopViewportInset) + horizontalModeExtraBottom;
  const measuredMobileBottomBase = mobileNavTopInset !== null
    ? `${mobileNavTopInset}px`
    : `var(--app-mobile-bottom-safe-offset, calc(env(safe-area-inset-bottom, 0px) + ${bottomNavHeight}px + 0.5rem))`;
  const mobileBottomOffsetCss = `calc(${measuredMobileBottomBase} + ${mobileFloatingGap} + ${storePublicRouteExtraGap})`;
  const containerStyle: React.CSSProperties = {
    bottom: isDesktop
      ? `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px))`
      : mobileBottomOffsetCss,
  };

  return (
    <>
      {pageEndSpacerHeight > 0 ? (
        <div
          aria-hidden="true"
          className="bg-background"
          style={{ height: `${pageEndSpacerHeight}px` }}
        />
      ) : null}

      <div
        data-floating-cart-button-root="true"
        className="fixed left-1/2 -translate-x-1/2 z-[9100] transition-[left,right,width,transform] duration-500 ease-out"
        style={containerStyle}
      >
      <button
        onClick={() => {
          openCheckoutSidebar({ cartOnly: true, preferredStoreId: displayedStoreId || undefined });
        }}
        className={`${showWideEndButton ? 'w-full' : isDesktop ? 'w-[min(92vw,32rem)]' : 'w-[min(92vw,22rem)]'} grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-3 bg-primary/90 supports-[backdrop-filter]:bg-primary/82 backdrop-blur-3xl text-white rounded-3xl shadow-lg hover:shadow-xl transition-[border-radius,padding,transform,box-shadow] duration-500 ease-out active:scale-[0.99] border border-[hsl(var(--primary)/0.34)] text-left ${isClosing ? 'popup-exit' : ''} ${!isClosing && firstAppear ? 'popup-appear' : ''}`}
      >
        <>
          <div className="min-w-0 ml-2 justify-self-start">
            <p
              className="text-[11px] font-semibold uppercase tracking-wide !text-white/85 leading-[1.1] truncate"
              style={{ maxWidth: showWideEndButton ? '42vw' : '9rem' }}
            >
              {storeDisplayLabel || 'Tu tienda'}
            </p>
          </div>

          <div className="shrink-0 flex items-center gap-2 justify-self-center">
            <div className="relative flex-shrink-0">
              {activeCartIsShared ? <Users className="h-4 w-4 !text-white/95 absolute -left-5 top-0.5" /> : null}
              <ShoppingCart className="h-5 w-5 !text-white -translate-x-1" />
              {displayedUnits > 0 ? (
                <span
                  className={`absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 bg-white text-primary text-[10px] font-bold flex items-center justify-center rounded-full ${badgePulse ? 'pulse' : ''}`}
                >
                  {displayedUnits > 99 ? '99+' : displayedUnits}
                </span>
              ) : null}
            </div>
            <p className="text-sm font-bold !text-white leading-tight whitespace-nowrap">{formatCurrency(displayedTotal)}</p>
          </div>

          <p className="shrink-0 mr-2 justify-self-end text-[12px] font-semibold !text-white/90 leading-none inline-flex items-center">
            Ir a caja
            <span
              className="inline-block ml-2"
              style={{
                animation: 'cartCtaArrowExit 3.2s cubic-bezier(0.22, 1, 0.36, 1) infinite',
              }}
            >
              &rarr;
            </span>
          </p>
        </>

      </button>

      <style>
        {`@keyframes cartCtaArrowExit {
          0% { transform: translateX(0); opacity: 0; }
          70% { transform: translateX(4px); opacity: 1; }
          88% { transform: translateX(4px); opacity: 1; }
          100% { transform: translateX(4px); opacity: 0; }
        }`}
      </style>
      </div>
    </>
  );
}
